import { useState } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { usePreservedCallback } from '@toss/react';
import { noop } from 'lodash-es';
import { Trans, useTranslation } from 'next-i18next';
import { usePhoneNumberPurchaseComplete, ZepApiError } from '@zep/apis';
import { useDialog } from '@zep/dialog';
import { Button, Checkbox, Dialog, DialogContent, DialogFooter, DialogHeader, DialogOverlay, DialogPanel, DialogTitle, Input } from '@zep/ui';
import { PlanTemplateType } from '../../../plan-payment';
type PhoneNumberInputDialogProps = {
  spaceHashId: string;
  planTemplateType: PlanTemplateType;
  spaceName?: string;
  spaceUrl?: string;
  planType?: string;
};
const PhoneNumberInputDialogInner = (props: PhoneNumberInputDialogProps) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    remove,
    hide,
    visible
  } = useModal();
  const dialog = useDialog();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isAgree, setIsAgree] = useState(false);
  const {
    mutateAsync: postPhoneNumber
  } = usePhoneNumberPurchaseComplete(props.spaceHashId);
  const noInputConfirm = usePreservedCallback(() => dialog.confirm({
    title: t('subscription:payment.phoneNumberModal.alertTitle'),
    content: t('subscription:payment.phoneNumberModal.alertContent'),
    confirmVariant: 'alert',
    confirmText: t('subscription:payment.phoneNumberModal.alertCancelText'),
    cancelText: t('subscription:payment.phoneNumberModal.alertConfirmText')
  }));
  const handleClickClose = async () => {
    const isSkip = await noInputConfirm();
    if (isSkip) {
      await hide();
    }
  };
  const handleChangeCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgree(e.target.checked);
  };
  const handleOnInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''));
  };
  const saveToGoogleSheet = async (_phoneNumber: string) => {
    // 구글 시트에 데이터를 저장하는 로직을 추가합니다.
    const url = 'https://docs.google.com/forms/d/e/1FAIpQLScZj0zLuz3YBNfSQDtpqpmx4bL29h309cWbIoqw1bvmtLtQFQ/formResponse';
    const params = new URLSearchParams({
      // spaceHashId
      'entry.1185996365': props.spaceHashId,
      // spaceName
      'entry.1904238534': props.spaceName ?? '',
      // spaceUrl
      'entry.116599576': props.spaceUrl ?? '',
      // planType
      'entry.1250612277': props.planType ?? '',
      // phoneNumber
      'entry.432418268': _phoneNumber
    });
    try {
      await fetch(url, {
        mode: 'no-cors',
        method: 'POST',
        body: params
      });
    } catch (error) {
      console.error('Submission to Google Sheets may have failed:', error);
    }
  };
  const handleClickConfirm = async () => {
    if (!phoneNumber) {
      const isSkip = await noInputConfirm();
      if (isSkip) {
        await hide();
      }
      return;
    }
    try {
      if (props.planTemplateType === PlanTemplateType.PRODUCT_ONETIME) {
        await saveToGoogleSheet(phoneNumber);
      } else {
        await postPhoneNumber(phoneNumber);
      }
      await hide();
    } catch (error: unknown) {
      const confirmed = await dialog.confirm({
        title: t('subscription:payment.phoneNumberModal.failConfirmTitle'),
        content: t('subscription:payment.phoneNumberModal.failConfirmContent', {
          statusKey: (error as ZepApiError)?.statusKey ?? 'UNKNOWN_ERROR'
        }),
        cancelText: t('subscription:payment.alertFailPay.buttonContact'),
        confirmText: t('subscription:paymentPayple.AlertFailPay.buttonConfirm')
      });
      if (!confirmed) {
        window.open(t('subscription:plans.contact.channelTalkLink'), '_blank');
      }
    }
  };
  return <Dialog drawer open={visible} onClose={noop} afterClose={remove} maxWidth={'400px'} data-sentry-element="Dialog" data-sentry-component="PhoneNumberInputDialogInner" data-sentry-source-file="PhoneNumberInputDialog.tsx">
      <DialogPanel data-sentry-element="DialogPanel" data-sentry-source-file="PhoneNumberInputDialog.tsx">
        <DialogHeader onClose={handleClickClose} data-sentry-element="DialogHeader" data-sentry-source-file="PhoneNumberInputDialog.tsx">
          <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="PhoneNumberInputDialog.tsx">
            {t('subscription:payment.phoneNumberModal.modalTitle')}
          </DialogTitle>
        </DialogHeader>
        <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="PhoneNumberInputDialog.tsx">
          <div className="flex flex-col gap-[16px]">
            <div className="min-h-[107px]">
              <img src="/images/checkout/collect-phone-number.png" alt="collect-number-img" />
            </div>
            <div>
              <div>
                {t('subscription:payment.phoneNumberModal.description')}
              </div>
              <Trans t={t} i18nKey={'subscription:payment.phoneNumberModal.description2'} components={{
              B: <span className="font-bold" />
            }} data-sentry-element="Trans" data-sentry-source-file="PhoneNumberInputDialog.tsx" />
            </div>
            <Input type="text" maxLength={18} placeholder={t('subscription:payment.phoneNumberModal.placeholder')} isFullWidth value={phoneNumber} onInput={handleOnInput} data-sentry-element="Input" data-sentry-source-file="PhoneNumberInputDialog.tsx" />
          </div>
        </DialogContent>
        <DialogFooter data-sentry-element="DialogFooter" data-sentry-source-file="PhoneNumberInputDialog.tsx">
          <div className="flex flex-col gap-[24px]">
            <Checkbox label={t('subscription:payment.phoneNumberModal.checkboxText')} onChange={handleChangeCheck} data-sentry-element="Checkbox" data-sentry-source-file="PhoneNumberInputDialog.tsx" />
            <Button onClick={handleClickConfirm} disabled={!isAgree} data-sentry-element="Button" data-sentry-source-file="PhoneNumberInputDialog.tsx">
              {t('subscription:payment.alertFailCardRegister.confirm')}
            </Button>
          </div>
        </DialogFooter>
      </DialogPanel>
      <DialogOverlay data-sentry-element="DialogOverlay" data-sentry-source-file="PhoneNumberInputDialog.tsx" />
    </Dialog>;
};
export const PhoneNumberInputDialog = NiceModal.create<PhoneNumberInputDialogProps>(PhoneNumberInputDialogInner);